import React from 'react';
import { CheckCircle } from 'lucide-react';

export const jobs = [
  {
    id: "business-development-manager",
    title: "Business Development Manager",
    department: "Business Development",
    location: "Yaoundé, Cameroon",
    type: "Full-Time",
    description: "Cirronyx is seeking an ambitious and results-driven Business Development Manager to accelerate our growth by identifying new business opportunities, building strategic partnerships, and expanding our client portfolio.",
    keywords: "Business development manager jobs Yaounde, tech jobs Cameroon, enterprise sales Cameroon, AI consulting jobs Yaounde",
    content: (
      <>
        <h4 className="font-bold text-slate-900 mb-4">About Cirronyx</h4>
        <p className="text-slate-600 mb-6 leading-relaxed">
          Cirronyx is an innovative technology company delivering Artificial Intelligence, software engineering, cloud solutions, automation, and digital transformation services to businesses across Africa. We help organizations leverage technology to improve efficiency, increase revenue, and solve complex business challenges.
        </p>

        <h4 className="font-bold text-slate-900 mb-4">Position Summary</h4>
        <p className="text-slate-600 mb-6 leading-relaxed">
          The Business Development Manager will be responsible for developing and executing business growth strategies, generating qualified sales opportunities, nurturing client relationships, negotiating contracts, and contributing to the company's long-term commercial success.<br /><br />
          This position requires someone who combines strategic thinking with strong networking, negotiation, and consultative selling skills.
        </p>

        <h4 className="font-bold text-slate-900 mb-4">Key Responsibilities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Identify and pursue new business opportunities across public and private sectors.",
            "Develop and execute business development and market expansion strategies.",
            "Generate qualified leads through networking, referrals, events, and outbound prospecting.",
            "Build and maintain strong relationships with decision-makers and key stakeholders.",
            "Prepare and deliver compelling business presentations and product demonstrations.",
            "Develop proposals, quotations, and commercial offers.",
            "Negotiate contracts and close new business deals.",
            "Collaborate closely with technical teams to design client-focused technology solutions.",
            "Monitor market trends, competitor activities, and emerging opportunities.",
            "Manage and maintain an accurate sales pipeline and CRM records.",
            "Represent Cirronyx at conferences, exhibitions, networking events, and industry forums.",
            "Establish strategic partnerships with corporations, government institutions, startups, NGOs, and international organizations.",
            "Prepare regular sales forecasts, performance reports, and market intelligence updates.",
            "Meet and exceed monthly and quarterly revenue targets."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">Required Qualifications</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Bachelor's degree in Business Administration, Marketing, Sales, Information Technology, Computer Science, or a related field.",
            "Ideally 3+ years of experience in business development, enterprise sales, account management, or technology consulting (highly motivated candidates with less experience are also encouraged to apply).",
            "Strong understanding of digital transformation, cloud services, software solutions, or Artificial Intelligence is an advantage.",
            "Excellent communication and presentation skills.",
            "Strong negotiation and relationship management abilities.",
            "Demonstrated ability to meet or exceed sales targets.",
            "Experience responding to RFPs, tenders, and commercial proposals is an added advantage.",
            "Fluency in English is required. Working knowledge of French is highly desirable."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">Required Skills</h4>
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            "Business Development", "Strategic Sales", "Enterprise Sales", "Lead Generation", 
            "Account Management", "Negotiation", "Microsoft Office & Google Workspace tools", 
            "CRM Management", "Market Research", "Proposal Writing", "Client Relationship Management", 
            "Public Speaking", "Commercial Strategy", "Networking", "Time Management", "Problem Solving"
          ].map((skill, idx) => (
            <span key={idx} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">
              {skill}
            </span>
          ))}
        </div>

        <h4 className="font-bold text-slate-900 mb-4">Key Performance Indicators (KPIs)</h4>
        <ul className="space-y-3 mb-6">
          {[
            "New client acquisition",
            "Monthly and quarterly revenue targets",
            "Sales pipeline growth",
            "Proposal conversion rate",
            "Client retention",
            "Strategic partnerships established",
            "Customer satisfaction",
            "Market expansion initiatives"
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">What We Offer</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Competitive salary with performance-based incentives.",
            "Career growth and leadership opportunities.",
            "Professional development and continuous learning.",
            "Exposure to cutting-edge AI and digital transformation projects.",
            "Collaborative and innovative work environment."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">How to Apply</h4>
        <p className="text-slate-600 mb-6">
          To apply for this position, simply click the <strong>"Apply Now"</strong> button on this page. You will be asked to fill out our application form with your contact details, upload your CV/Resume, and provide an optional cover letter.
        </p>

        <p className="text-slate-600 italic mb-2">
          Only shortlisted candidates will be contacted.
        </p>
      </>
    )
  },
  {
    id: "software-engineering-intern",
    title: "Software Engineering Intern",
    department: "Engineering",
    location: "Onsite — Entrée St Marc, Biyem-Assi, Yaoundé",
    type: "Internship",
    description: "Bring what you've learned in school into a real-world environment and grow your coding skills in Cameroon. Join Cirronyx as a Software Engineering Intern in Yaounde.",
    keywords: "internship in Yaounde, interns in Yaounde, software engineering intern Yaounde, IT internship Cameroon, web development intern Yaounde",
    content: (
      <>
        <h4 className="font-bold text-slate-900 mb-4">About the Role</h4>
        <p className="text-slate-600 mb-6 leading-relaxed">
          We are looking for an enthusiastic Software Engineering Intern to join our team. In this role, you will work alongside our developers to help build and test software applications. This is a great space to bring what you’ve learned in school into a real-world environment and grow your coding skills.
        </p>

        <h4 className="font-bold text-slate-900 mb-4">Key Responsibilities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Assist in writing, testing, and debugging code.",
            "Join our team meetings to learn about project planning and software design.",
            "Help document how our software works.",
            "Collaborate with the team to brainstorm and build new features."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">Requirements & Formalities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Background: Currently enrolled in a higher institute or a recent graduate.",
            "Skills: A basic understanding of at least one programming language (like Python, JavaScript, Java, or C++) and a willingness to learn more.",
            "Academic Interns (1-3 Months): Please provide an internship request letter from your school.",
            "Professional Interns (6-12 Months): Please provide a CV. If you have a GitHub or a small project you’ve worked on, we’d love to see it!"
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">What We Offer</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Real Experience: Work on real tasks that actually help our business grow.",
            "Guidance & Support: Learn directly from our team in a friendly, no-pressure environment.",
            "A Great Workspace: Join us at our easily accessible office in Biyem-Assi.",
            "Future Opportunities: A certificate of completion when you finish, and the potential to join us full-time for outstanding professional interns."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">How to Apply</h4>
        <p className="text-slate-600 mb-6">
          To apply for this position, simply click the <strong>"Apply Now"</strong> button on this page. You will be asked to fill out our application form with your contact details, upload your CV, and provide your academic letter (if applicable).
        </p>
      </>
    )
  },
  {
    id: "marketing-intern",
    title: "Marketing Intern",
    department: "Marketing",
    location: "Onsite — Entrée St Marc, Biyem-Assi, Yaoundé",
    type: "Internship",
    description: "Eager to learn how businesses grow, brainstorm ideas, and see firsthand how digital campaigns are built? Join Cirronyx as a Marketing Intern in Yaounde, Cameroon.",
    keywords: "internship in Yaounde, interns in Yaounde, marketing intern Yaounde, digital marketing internship Cameroon, social media intern Yaounde",
    content: (
      <>
        <h4 className="font-bold text-slate-900 mb-4">About the Role</h4>
        <p className="text-slate-600 mb-6 leading-relaxed">
          We are seeking a creative Marketing Intern who is eager to learn how businesses grow. You will help us brainstorm ideas, create social media posts, and see firsthand how digital marketing campaigns are put together.
        </p>

        <h4 className="font-bold text-slate-900 mb-4">Key Responsibilities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Help create fun and engaging posts for our social media pages.",
            "Learn how to look at market trends and see what other companies are doing.",
            "Assist the team in organizing small marketing events or online campaigns.",
            "Help check how well our posts and emails are doing."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">Requirements & Formalities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Background: Currently enrolled in a higher institute or a recent graduate.",
            "Skills: Good communication skills, a love for social media, and an organized mindset.",
            "Academic Interns (1-3 Months): Please provide an internship request letter from your school.",
            "Professional Interns (6-12 Months): Please provide a CV. If you have any writing samples or pages you've managed, feel free to share them."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">What We Offer</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Real Experience: Work on real tasks that actually help our business grow.",
            "Guidance & Support: Learn directly from our team in a friendly, no-pressure environment.",
            "A Great Workspace: Join us at our easily accessible office in Biyem-Assi.",
            "Future Opportunities: A certificate of completion when you finish, and the potential to join us full-time for outstanding professional interns."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">How to Apply</h4>
        <p className="text-slate-600 mb-6">
          To apply for this position, simply click the <strong>"Apply Now"</strong> button on this page. You will be asked to fill out our application form with your contact details, upload your CV, and provide your academic letter (if applicable).
        </p>
      </>
    )
  },
  {
    id: "graphics-and-web-design-intern",
    title: "Graphics and Web Design Intern",
    department: "Design",
    location: "Onsite — Entrée St Marc, Biyem-Assi, Yaoundé",
    type: "Internship",
    description: "Help our team design digital graphics and website layouts, building your portfolio with real company projects. Join Cirronyx as a Graphics and Web Design Intern in Yaounde, Cameroon.",
    keywords: "internship in Yaounde, interns in Yaounde, graphics design intern Yaounde, web design internship Cameroon, UI UX intern Yaounde",
    content: (
      <>
        <h4 className="font-bold text-slate-900 mb-4">About the Role</h4>
        <p className="text-slate-600 mb-6 leading-relaxed">
          We are looking for a creative Graphics and Web Design Intern with an eye for visuals. You will help our team design everything from social media graphics to website layouts. It’s a fantastic opportunity to build your portfolio with real company projects.
        </p>

        <h4 className="font-bold text-slate-900 mb-4">Key Responsibilities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Help design digital flyers, web banners, and social media images.",
            "Learn about making websites look good and easy to use (UI/UX basics).",
            "Assist in creating drafts and mockups for new design ideas.",
            "Work closely with the marketing team to keep our brand looking sharp."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">Requirements & Formalities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Background: Currently enrolled in a higher institute or a recent graduate.",
            "Skills: Some familiarity with design tools (like Canva, Photoshop, Illustrator, or Figma). An interest in learning web design basics is a bonus!",
            "Academic Interns (1-3 Months): Please provide an internship request letter from your school.",
            "Professional Interns (6-12 Months): Please provide a CV and a few examples of your design work (even school projects are great)."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">What We Offer</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Real Experience: Work on real tasks that actually help our business grow.",
            "Guidance & Support: Learn directly from our team in a friendly, no-pressure environment.",
            "A Great Workspace: Join us at our easily accessible office in Biyem-Assi.",
            "Future Opportunities: A certificate of completion when you finish, and the potential to join us full-time for outstanding professional interns."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">How to Apply</h4>
        <p className="text-slate-600 mb-6">
          To apply for this position, simply click the <strong>"Apply Now"</strong> button on this page. You will be asked to fill out our application form with your contact details, upload your CV/portfolio, and provide your academic letter (if applicable).
        </p>
      </>
    )
  },
  {
    id: "printing-intern",
    title: "Printing Intern",
    department: "Operations",
    location: "Onsite — Entrée St Marc, Biyem-Assi, Yaoundé",
    type: "Internship",
    description: "Learn the practical side of commercial printing and see how designs turn into high-quality physical products. Join Cirronyx as a Printing Intern in Yaounde, Cameroon.",
    keywords: "internship in Yaounde, interns in Yaounde, printing intern Yaounde, commercial printing internship Cameroon",
    content: (
      <>
        <h4 className="font-bold text-slate-900 mb-4">About the Role</h4>
        <p className="text-slate-600 mb-6 leading-relaxed">
          We are looking for a hands-on Printing Intern to learn the practical side of commercial printing. You will get to see how designs on a screen turn into high-quality physical products and learn how to operate professional printing equipment.
        </p>

        <h4 className="font-bold text-slate-900 mb-4">Key Responsibilities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Learn how to prepare digital files for printing (checking colors and sizes).",
            "Assist in operating and taking care of our printing machines.",
            "Help the team organize print materials and keep track of supplies.",
            "Check finished prints to make sure they look great before they go to clients."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">Requirements & Formalities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Background: Currently enrolled in a higher institute or a recent graduate.",
            "Skills: Detail-oriented, ready to work with your hands, and eager to learn the printing process. Basic computer skills are helpful.",
            "Academic Interns (1-3 Months): Please provide an internship request letter from your school.",
            "Professional Interns (6-12 Months): Please provide a CV highlighting any relevant interests or hands-on skills."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">What We Offer</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Real Experience: Work on real tasks that actually help our business grow.",
            "Guidance & Support: Learn directly from our team in a friendly, no-pressure environment.",
            "A Great Workspace: Join us at our easily accessible office in Biyem-Assi.",
            "Future Opportunities: A certificate of completion when you finish, and the potential to join us full-time for outstanding professional interns."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">How to Apply</h4>
        <p className="text-slate-600 mb-6">
          To apply for this position, simply click the <strong>"Apply Now"</strong> button on this page. You will be asked to fill out our application form with your contact details, upload your CV, and provide your academic letter (if applicable).
        </p>
      </>
    )
  },
  {
    id: "network-and-cyber-security-intern",
    title: "Network and Cyber Security Intern",
    department: "IT Security",
    location: "Onsite — Entrée St Marc, Biyem-Assi, Yaoundé",
    type: "Internship",
    description: "Learn how to protect digital information and understand how secure networks operate. Join Cirronyx as a Network and Cyber Security Intern in Yaounde, Cameroon.",
    keywords: "internship in Yaounde, interns in Yaounde, cyber security intern Yaounde, network intern Yaounde, IT security internship Cameroon",
    content: (
      <>
        <h4 className="font-bold text-slate-900 mb-4">About the Role</h4>
        <p className="text-slate-600 mb-6 leading-relaxed">
          We are seeking an inquisitive Network and Cyber Security Intern to learn how to protect digital information. You will work closely with our IT team to understand how networks run and how to keep company data safe from online threats.
        </p>

        <h4 className="font-bold text-slate-900 mb-4">Key Responsibilities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Learn how to check our network for any unusual activity.",
            "Assist in setting up basics like routers and safe connections.",
            "Help the team run basic security checks on our systems.",
            "Read up on and help update our IT safety rules."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">Requirements & Formalities</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Background: Currently enrolled in a higher institute or a recent graduate.",
            "Skills: A basic understanding of computers, how the internet connects, and a strong interest in learning about online security and privacy.",
            "Academic Interns (1-3 Months): Please provide an internship request letter from your school.",
            "Professional Interns (6-12 Months): Please provide a CV. Any IT certificates or completed online courses are a nice bonus!"
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">What We Offer</h4>
        <ul className="space-y-3 mb-6">
          {[
            "Real Experience: Work on real tasks that actually help our business grow.",
            "Guidance & Support: Learn directly from our team in a friendly, no-pressure environment.",
            "A Great Workspace: Join us at our easily accessible office in Biyem-Assi.",
            "Future Opportunities: A certificate of completion when you finish, and the potential to join us full-time for outstanding professional interns."
          ].map((req, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>{req}</span>
            </li>
          ))}
        </ul>

        <h4 className="font-bold text-slate-900 mb-4">How to Apply</h4>
        <p className="text-slate-600 mb-6">
          To apply for this position, simply click the <strong>"Apply Now"</strong> button on this page. You will be asked to fill out our application form with your contact details, upload your CV, and provide your academic letter (if applicable).
        </p>
      </>
    )
  }
];
